import { useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '~/components/ui/dropdown-menu';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Textarea } from '~/components/ui/textarea';
import { PaperSignatureDialog } from './PaperSignatureDialog';
import { SetTuitionDialog, type TuitionPlan } from './SetTuitionDialog';
import { centsToDollars, dollarsToCents } from './moneyInput';
import { useMarkInvoicePaidOutOfBand } from './useBillingInvoiceActions';
import { useChangeChecklistItem } from './useChangeChecklistItem';

export interface ChecklistMenuItem {
  id: string;
  title: string;
  state: string;
  stepType?: string | null;
  /** The agreement step's offer; the office-only fields reach admins alone. */
  tuitionAgreement?: { agreementId: string; state?: string; paymentOptionKey?: string | null; aidCents: number; aidReason?: string | null } | null;
  /** The fee step's invoice, once there is one. */
  feeInvoiceId?: string | null;
}

type Change = 'MARK_DONE' | 'WAIVE' | 'REOPEN' | 'SET_TUITION' | 'CHANGE_TUITION' | 'PAPER_SIGNATURE' | 'RECORD_CHECK';

const MONEY_STEPS = ['agreement', 'auto_pay', 'fee'];

/** The words for an action; the same Reopen is "Turn off auto-pay" on auto-pay that is on. */
function changeLabel(change: Change, item: ChecklistMenuItem): string {
  switch (change) {
    case 'MARK_DONE': return 'Mark done';
    case 'WAIVE': return 'Waive';
    case 'SET_TUITION': return 'Set tuition';
    case 'CHANGE_TUITION': return 'Change tuition';
    case 'RECORD_CHECK': return 'Record a check';
    case 'PAPER_SIGNATURE': return item.stepType === 'auto_pay' ? 'Record paper authorization' : 'Record paper signature';
    case 'REOPEN': return item.stepType === 'auto_pay' && item.state === 'DONE' ? 'Turn off auto-pay' : 'Reopen';
  }
}

/**
 * What the office can do to an item in this state; none for history or a step that isn't needed.
 * The money steps mirror the server: Mark done is refused on them (each has its own action),
 * a signed agreement and a paid fee can't be reopened, and Reopen on auto-pay that is on turns it off.
 * Once the family can't change Who's coming (the year is enrolled or its agreement signed), it can't be reopened either.
 */
export function itemChanges(item: ChecklistMenuItem, { whoClosed = false }: { whoClosed?: boolean } = {}): Change[] {
  if (item.stepType === 'legacy_record') return [];
  if (item.stepType === 'who' && whoClosed && ['DONE', 'WAIVED'].includes(item.state)) return [];
  const waiting = ['OPEN', 'IN_PROGRESS'].includes(item.state);
  if (MONEY_STEPS.includes(item.stepType ?? '')) {
    if (item.state === 'WAIVED') return ['REOPEN'];
    if (item.state === 'DONE') {
      if (item.stepType === 'auto_pay') return ['REOPEN'];
      // Only the year's signed agreement can be changed; one a later agreement replaced stays history.
      return item.stepType === 'agreement' && item.tuitionAgreement?.state === 'SIGNED' ? ['CHANGE_TUITION'] : [];
    }
    // A family that asked for aid leaves the agreement a draft the office sets; the server reports that step as LOCKED.
    if (item.stepType === 'agreement' && item.tuitionAgreement?.state === 'DRAFT') return ['SET_TUITION', 'WAIVE'];
    if (item.state === 'LOCKED') return ['WAIVE'];
    if (!waiting) return [];
    if (item.stepType === 'agreement') return item.tuitionAgreement ? ['SET_TUITION', 'PAPER_SIGNATURE', 'WAIVE'] : ['WAIVE'];
    if (item.stepType === 'auto_pay') return ['PAPER_SIGNATURE', 'WAIVE'];
    return item.feeInvoiceId ? ['RECORD_CHECK', 'WAIVE'] : ['WAIVE'];
  }
  if (['OPEN', 'IN_PROGRESS', 'LOCKED'].includes(item.state)) return ['MARK_DONE', 'WAIVE'];
  if (['DONE', 'WAIVED'].includes(item.state)) return ['REOPEN'];
  return [];
}

interface RecordCheckDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: ChecklistMenuItem;
  amountCents: number | null;
  onSaved?: () => void;
}

// The office records a check for the fee's invoice, through the Billing tab's own mutation.
function RecordCheckDialog({ open, onOpenChange, item, amountCents, onSaved }: RecordCheckDialogProps) {
  const [amount, setAmount] = useState(amountCents === null ? '' : centsToDollars(amountCents));
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [markPaid, { loading }] = useMarkInvoicePaidOutOfBand();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const cents = dollarsToCents(amount);
    if (!item.feeInvoiceId || cents === null || cents <= 0) {
      setErrors(['Amount must be greater than $0.']);
      return;
    }
    try {
      const result = await markPaid({
        variables: { input: { invoiceId: item.feeInvoiceId, amountCents: cents, paymentMethod: 'CHECK', note: note.trim() || null } },
      });
      const payload = result.data?.markInvoicePaidOutOfBand;
      const userErrors = payload?.userErrors ?? [];
      if (userErrors.length > 0 || payload?.success === false) {
        setErrors(userErrors.length > 0 ? userErrors.map((e) => e.message) : ['The check was not recorded. Please try again.']);
        return;
      }
      setErrors([]);
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'The check was not recorded. Please try again.']);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) setErrors([]); onOpenChange(next); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Record a check</DialogTitle>
          <DialogDescription>{item.title}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {errors.length > 0 && (
            <div role="alert" className="space-y-1 text-sm text-destructive">
              {errors.map((message) => (
                <p key={message}>{message}</p>
              ))}
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor={`check-amount-${item.id}`}>Amount</Label>
            <Input id={`check-amount-${item.id}`} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`check-note-${item.id}`}>Check number or note (optional)</Label>
            <Input id={`check-note-${item.id}`} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              Record a check
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface ChecklistItemMenuProps {
  item: ChecklistMenuItem;
  familyEnrollmentId?: string;
  /** The year's plans, for Set tuition. */
  plans?: TuitionPlan[];
  /** The family year's invoices, to find the fee's amount for Record a check. */
  invoices?: { id: string; amountCents: number }[];
  /** The family can't change Who's coming any more: the year is enrolled or its agreement signed. */
  whoClosed?: boolean;
  onChanged?: () => void;
}

// The office's menu on one checklist item. The server's answer decides: a
// refusal stays in the dialog and the dialog stays open.
export function ChecklistItemMenu({ item, familyEnrollmentId = '', plans = [], invoices = [], whoClosed = false, onChanged }: ChecklistItemMenuProps) {
  const [action, setAction] = useState<Change | null>(null);
  const change = action === 'MARK_DONE' || action === 'WAIVE' || action === 'REOPEN' ? action : null;
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [changeItem, { loading }] = useChangeChecklistItem();
  const changes = itemChanges(item, { whoClosed });
  if (changes.length === 0) return null;

  const close = () => {
    setAction(null);
    setNote('');
    setErrors([]);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!change) return;
    if (change === 'WAIVE' && !note.trim()) {
      setErrors(["Say why it's waived"]);
      return;
    }
    try {
      const result = await changeItem({ variables: { input: { itemId: item.id, change, note: note.trim() || null } } });
      const serverErrors = result.data?.changeChecklistItem?.errors ?? [];
      if (serverErrors.length > 0) {
        setErrors(serverErrors);
        return;
      }
      close();
      onChanged?.();
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'The change was not saved. Please try again.']);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="shrink-0" aria-label={`Actions for ${item.title}`}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {changes.map((option) => (
            <DropdownMenuItem key={option} onSelect={() => setAction(option)}>
              {changeLabel(option, item)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={change !== null} onOpenChange={(next) => { if (!next) close(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{change && changeLabel(change, item)}</DialogTitle>
            <DialogDescription>{item.title}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {errors.length > 0 && (
              <div role="alert" className="space-y-1 text-sm text-destructive">
                {errors.map((message) => (
                  <p key={message}>{message}</p>
                ))}
              </div>
            )}
            {change && change !== 'REOPEN' && (
              <div className="space-y-1">
                <Label htmlFor={`item-note-${item.id}`}>{change === 'WAIVE' ? 'Reason' : 'Note (optional)'}</Label>
                <Textarea id={`item-note-${item.id}`} value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {change && changeLabel(change, item)}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {(action === 'SET_TUITION' || action === 'CHANGE_TUITION') && (
        <SetTuitionDialog open onOpenChange={(next) => { if (!next) close(); }} item={item} plans={plans} familyEnrollmentId={familyEnrollmentId} onSaved={onChanged} />
      )}
      {action === 'PAPER_SIGNATURE' && (
        <PaperSignatureDialog
          open
          onOpenChange={(next) => { if (!next) close(); }}
          item={item}
          agreementId={item.tuitionAgreement?.agreementId ?? null}
          familyEnrollmentId={familyEnrollmentId}
          onSaved={onChanged}
        />
      )}
      {action === 'RECORD_CHECK' && (
        <RecordCheckDialog
          open
          onOpenChange={(next) => { if (!next) close(); }}
          item={item}
          amountCents={invoices.find((invoice) => invoice.id === item.feeInvoiceId)?.amountCents ?? null}
          onSaved={onChanged}
        />
      )}
    </>
  );
}
